import React from 'react';
import { Box, Typography, Stack, IconButton, Button, Divider, Drawer, Skeleton, ToggleButtonGroup, ToggleButton, Avatar } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSideBarHandler } from '../hooks/useSideBarHandler';
import { useChatSessionsProvider } from '../hooks/useChatSessionsProvider';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
// import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useChatSessionDeletehandler from '../hooks/useChatSessionDeletehandler';
import DeleteConfirmationModal from '../../../common-components/DeleteConfirmationModal';
import useNewChatHandler from '../../../containers/chat/hooks/useNewChatHandler';
import { AccountSelector } from './AccountSelector';
import useProductsProvider from '../hooks/useProductsProvider';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CallIcon from '@mui/icons-material/Call';
import moment from 'moment';
import { SESSION_TYPES } from '../../../containers/chat/slice/constants';
import { Menu } from '../../../common-components';
import LogoutIcon from '@mui/icons-material/Logout';
import LogoutConfirmationModal from './LogoutConfirmationModal';
import ConnectUser from './ConnectUser';
import { PRODUCTS, ROLES } from '../slice/constants';
import { ROUTE_PATHS } from '../../../routes/constants';
import { useNavigate } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

export function Sidebar() {
  const { isSidebarOpen, xsScreen, toggleSidebar, setInitiateLogout, handleLogout, initiateLogout, isLogoutInProgress, currentUser } = useSideBarHandler();

  const { chatSessions, currentSessionId, handleSessionClick, isChatSessionsLoading, historyType, handleHistoryTypeChange } = useChatSessionsProvider();
  const { handleDelete, chatSessionIDToDelete, handleDeleteConfirmation, handleDeleteCancel } = useChatSessionDeletehandler();
  const { handleNewChat } = useNewChatHandler();
  const sidebarWidth = isSidebarOpen ? 280 : xsScreen ? 0 : 60;
  const navigate = useNavigate();
  const { products, currentProduct, setCurrentProduct } = useProductsProvider();

  const onClickNewChat = () => {
    handleNewChat();
    if (xsScreen) {
      toggleSidebar(false);
    }
  };

  const onClickSession = (sessionId: string) => {
    handleSessionClick(sessionId);
    if (xsScreen) {
      toggleSidebar(false);
    }
  };

  return (
    <Drawer open={true} anchor="left" variant={xsScreen && isSidebarOpen ? 'temporary' : 'permanent'} ModalProps={{ keepMounted: true }}>
      <Box
        sx={{
          width: `${sidebarWidth}px`,
          backgroundColor: 'background.default',
        }}
      >
        <Box
          sx={{
            height: '100vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isSidebarOpen ? 'unset' : 'center',
            position: 'relative',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 0.5, justifyContent: isSidebarOpen ? 'space-between' : 'center' }}>
            <AccountSelector accounts={products} selectedId={currentProduct} onChange={(id) => setCurrentProduct(id)} isSidebarOpen={isSidebarOpen} currentUser={currentUser} />

            <IconButton onClick={() => toggleSidebar()} sx={isSidebarOpen ? {} : { display: 'none' }}>
              <MenuOpenIcon sx={{ transform: isSidebarOpen ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.3s ease-in-out' }} />
            </IconButton>
          </Box>

          <Divider />

          {/* New Conversation */}
          {isSidebarOpen ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={onClickNewChat}
              sx={{
                p: 1.25,
                borderRadius: 2,
                m: 2,
                // border: "none",
                color: 'text.secondary',
                textAlign: 'left',
              }}
              startIcon={<OpenInNewIcon />}
            >
              New Conversation
            </Button>
          ) : (
            <IconButton size="small" aria-label="New chat" sx={{ mt: 2, mx: 'auto' }} onClick={onClickNewChat}>
              <OpenInNewIcon />
            </IconButton>
          )}
          <Divider />
          {currentUser?.role === ROLES.ADMIN && currentProduct === PRODUCTS.QRTIGER ? <ConnectUser /> : null}
          {currentUser?.role === ROLES.ADMIN && currentProduct === PRODUCTS.QRTIGER ? <Divider /> : null}
          {isSidebarOpen && (
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Recent Conversations
              </Typography>
              <Box sx={{ mt: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  color="primary"
                  value={historyType}
                  exclusive
                  onChange={(_, val) => {
                    if (val) handleHistoryTypeChange(val);
                  }}
                  fullWidth
                >
                  <ToggleButton value={SESSION_TYPES.CHAT}>Chats</ToggleButton>
                  <ToggleButton value={SESSION_TYPES.CALL}>Calls</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          )}

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 500px)', overflowY: 'auto' }}>
            {isChatSessionsLoading && isSidebarOpen && (
              <Box sx={{ px: 2, py: 0.5, width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Skeleton variant="text" width="100%" height={40} />
                <Skeleton variant="text" width="100%" height={40} />
                <Skeleton variant="text" width="100%" height={40} />
              </Box>
            )}
            {/* Chat Sessions */}
            {chatSessions.length > 0 && isSidebarOpen && !isChatSessionsLoading && (
              <>
                {chatSessions.map((session) => (
                  <Box key={session._id} sx={{ px: 2, py: 0.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: '0.2rem 0.5rem',
                        borderRadius: 2,
                        backgroundColor: currentSessionId === session._id ? 'grey.300' : 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: currentSessionId === session._id ? 'grey.100' : 'grey.50',
                        },
                      }}
                      onClick={() => onClickSession(session._id)}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                        {session.sessionType === SESSION_TYPES.CHAT ? <ChatBubbleOutlineIcon fontSize="small" color="action" /> : <CallIcon fontSize="small" color="action" />}
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            textTransform: 'capitalize',
                          }}
                        >
                          {session.sessionType === SESSION_TYPES.CALL ? (moment(session.title).isValid() ? moment(session.title).format('MMM D, YYYY • h:mm A') : session.title) : session.title}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Menu
                          trigger={
                            <IconButton size="small" id={session._id}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          }
                          items={[
                            {
                              icon: <DeleteIcon fontSize="small" />,
                              label: 'Delete',
                              onClick: () => {
                                handleDelete(session._id);
                              },
                            },
                          ]}
                        />
                      </Stack>
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Box>
          <Box sx={{ mt: 'auto' }}>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '0.5rem' }}>
              <Box>
                {isSidebarOpen && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    v1.0.0 (Beta)
                  </Typography>
                )}
              </Box>
              {!currentUser?.productId && (
                <Menu
                  trigger={<Avatar src={currentUser?.name} />}
                  items={[
                    ...(currentUser?.role === ROLES.ADMIN ? [{
                      icon: <AdminPanelSettingsIcon fontSize="small" />,
                      label: 'Admin Dashboard',
                      onClick: () => {
                        navigate(ROUTE_PATHS.ADMIN_DASHBOARD);
                      },
                    }] : []),
                    {
                      icon: <LogoutIcon fontSize="small" />,
                      label: 'Logout',
                      onClick: () => setInitiateLogout(true),
                    },
                  ]}
                />
              )}
            </Box>
          </Box>
        </Box>
        <DeleteConfirmationModal open={Boolean(chatSessionIDToDelete)} onClose={handleDeleteCancel} onDelete={handleDeleteConfirmation} loading={isChatSessionsLoading} />
        <LogoutConfirmationModal open={initiateLogout} onClose={() => setInitiateLogout(false)} onLogout={handleLogout} loading={isLogoutInProgress} />
      </Box>
    </Drawer>
  );
}
